/**
 * Level library (Brief 004 M2#8, decision M2-c: minimal new/open/delete/rename). A
 * localStorage-backed set of named drafts, separate from the single working-draft
 * autosave (EditorState.DRAFT_KEY). Each entry stores a full LevelFormat snapshot +
 * a display name + ISO save time. Open round-trips through parseLevel so a corrupt
 * entry can never reach the editor unvalidated.
 */
import type { LevelFormat } from '../engine/LevelFormat';
import { parseLevel } from './serialize';

const LIB_KEY = 'maker.editor.library.v1';

/** Lightweight listing row (no level payload). */
export interface LibraryEntry { id: string; name: string; savedAt: string; }
interface StoredEntry extends LibraryEntry { level: LevelFormat; }
type Store = Record<string, StoredEntry>;

function read(): Store {
  try { return JSON.parse(localStorage.getItem(LIB_KEY) ?? '{}') as Store; } catch { return {}; }
}
function write(store: Store): void {
  try { localStorage.setItem(LIB_KEY, JSON.stringify(store)); } catch { /* quota / private mode — non-fatal */ }
}

function newId(): string {
  return 'lvl_' + Date.now().toString(36) + '_' + Math.floor(Math.random() * 1e6).toString(36);
}

/** All entries, newest first (payload stripped). */
export function listLibrary(): LibraryEntry[] {
  return Object.values(read())
    .map(({ id, name, savedAt }) => ({ id, name, savedAt }))
    .sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

/** Save (or overwrite, when `id` is given) a level. Returns the entry id. */
export function saveToLibrary(level: LevelFormat, id?: string): string {
  const store = read();
  const key = id ?? newId();
  store[key] = { id: key, name: level.meta.name || 'Untitled', savedAt: new Date().toISOString(), level: structuredClone(level) };
  write(store);
  return key;
}

/** Load a validated copy of a stored level, or null if missing/corrupt. */
export function openFromLibrary(id: string): LevelFormat | null {
  const entry = read()[id];
  if (!entry) return null;
  const { level } = parseLevel(JSON.stringify(entry.level));
  return level ?? null;
}

export function removeFromLibrary(id: string): void {
  const store = read();
  delete store[id];
  write(store);
}

export function renameInLibrary(id: string, name: string): void {
  const store = read();
  if (store[id]) { store[id].name = name; store[id].level.meta.name = name; write(store); }
}
